/**
 * Enchantment helpers that read the player's Inventory (armor / hands):
 * protection EPF, Unbreaking-aware wear, Mending, Thorns, knockback resistance,
 * Silk Touch / Fortune block drops.
 */

import type { Inventory } from '../inventory';
import { itemDef, maxDamageOf } from '../itemdb';
import { dropsFor, experienceFor, blockItemOf, canHarvest } from '../mining';
import { isEmpty, stackOf, type DamageKind, type Stack } from '../types';
import { applyFortune, durabilityLoss, protectionEPF, silkTouchDrop } from './effects';
import { E } from './registry';
import { enchLevel } from './stack';

export function armorEPF(inv: Inventory, kind: DamageKind): number {
  return protectionEPF(inv.armor, kind);
}

/** Wear `s` by `amount` uses, honouring Unbreaking. Returns true if it broke. */
export function wearStack(inv: Inventory, s: Stack, amount: number, rnd: () => number = Math.random): boolean {
  if (isEmpty(s) || amount <= 0) return false;
  const n = durabilityLoss(s, amount, !!itemDef(s.id).armor, rnd);
  return n > 0 ? inv.damage(s, n) : false;
}

/** Total knockback resistance of the worn armor (netherite 0.1 per piece). */
export function knockbackResistance(inv: Inventory): number {
  let k = 0;
  for (const s of inv.armor) if (!isEmpty(s)) k += itemDef(s.id).armor?.knockbackResistance ?? 0;
  return Math.min(1, k);
}

/**
 * Mending: an XP orb repairs a random damaged Mending item in hand/offhand/armor,
 * 2 durability per XP point. Returns the XP left for the player.
 */
export function mendingAbsorb(inv: Inventory, xp: number, rnd: () => number = Math.random): number {
  const cands = [inv.held, inv.offhand, ...inv.armor].filter((s) => !isEmpty(s) && s.dmg > 0 && enchLevel(s, E.MENDING) > 0);
  if (!cands.length) return xp;
  const s = cands[Math.floor(rnd() * cands.length)];
  const repair = Math.min(xp * 2, s.dmg);
  s.dmg -= repair;
  inv.changed();
  return xp - Math.ceil(repair / 2);
}

/** Thorns: [damage to deal to the attacker] — each piece rolls 15%/level for 1..4 damage, and wears 2 extra. */
export function thornsDamage(inv: Inventory, rnd: () => number = Math.random): number {
  let dmg = 0;
  for (const s of inv.armor) {
    const l = enchLevel(s, E.THORNS);
    if (l <= 0 || isEmpty(s)) continue;
    if (rnd() < 0.15 * l) {
      dmg += l > 10 ? l - 10 : 1 + Math.floor(rnd() * 4);
      wearStack(inv, s, 2, rnd);
    }
  }
  return dmg;
}

/** Block drops + XP for a broken block with the held tool's Silk Touch / Fortune. */
export function enchantedDrops(blockId: number, held: Stack, rnd: () => number): { drops: Stack[]; xp: number } {
  const heldId = isEmpty(held) ? 0 : held.id;
  if (!canHarvest(blockId, heldId)) return { drops: [], xp: 0 };
  if (enchLevel(held, E.SILK_TOUCH) > 0) {
    const normal = dropsFor(blockId, heldId, rnd);
    const silk = silkTouchDrop(blockId, -1);
    if (silk > 0) return { drops: [stackOf(blockItemOf(silk))], xp: 0 };
    return { drops: normal, xp: experienceFor(blockId, rnd) };
  }
  const drops = applyFortune(blockId, dropsFor(blockId, heldId, rnd), enchLevel(held, E.FORTUNE), rnd);
  return { drops, xp: experienceFor(blockId, rnd) };
}

export const maxDur = maxDamageOf;
